import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Template {
  id: string;
  title: string;
  category: string;
  description: string;
  itemCount: number;
}

interface Checklist {
  id: string;
  title: string;
  category: string;
  dependent?: { id: string; name: string } | null;
  totalCount: number;
  completedCount: number;
  createdAt: string;
}

interface Dependent {
  id: string;
  name: string;
  isActive: boolean;
}

const CATEGORY_LABEL: Record<string, string> = {
  HOME_SAFETY: 'Home Safety',
  HOSPITAL_DISCHARGE: 'Hospital Discharge',
  EMERGENCY_PLAN: 'Emergency Plan',
  MEDICATION_REVIEW: 'Medication Review',
};

const CATEGORY_COLOUR: Record<string, string> = {
  HOME_SAFETY: 'bg-blue-100 text-blue-800',
  HOSPITAL_DISCHARGE: 'bg-purple-100 text-purple-800',
  EMERGENCY_PLAN: 'bg-red-100 text-red-800',
  MEDICATION_REVIEW: 'bg-teal-100 text-teal-800',
};

// ─── StartChecklistForm ───────────────────────────────────────────────────────

function StartChecklistForm({
  template,
  familyId,
  dependents,
  onSuccess,
  onCancel,
}: {
  template: Template;
  familyId: string;
  dependents: Dependent[];
  onSuccess: (id: string) => void;
  onCancel: () => void;
}) {
  const [dependentId, setDependentId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleStart = async () => {
    setSaving(true);
    setError('');
    try {
      const list = await api.createChecklist(familyId, {
        templateId: template.id,
        dependentId: dependentId || undefined,
      });
      onSuccess(list.id);
    } catch {
      setError('Failed to create checklist');
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 p-4 border border-dashed border-gray-300 rounded-xl bg-gray-50">
      <p className="text-sm font-medium text-gray-800 mb-3">Start: {template.title}</p>
      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
      {dependents.length > 0 && (
        <div className="mb-3">
          <label className="block text-xs text-gray-500 mb-1">Link to a dependent (optional)</label>
          <select
            value={dependentId}
            onChange={(e) => setDependentId(e.target.value)}
            className="input w-full text-sm"
          >
            <option value="">Family (not specific to one person)</option>
            {dependents.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>
      )}
      <div className="flex gap-2">
        <button onClick={handleStart} disabled={saving} className="btn btn-primary flex-1 text-sm">
          {saving ? 'Creating…' : 'Start checklist'}
        </button>
        <button onClick={onCancel} className="btn flex-1 text-sm">Cancel</button>
      </div>
    </div>
  );
}

// ─── ChecklistsPage ───────────────────────────────────────────────────────────

export function ChecklistsPage() {
  const navigate = useNavigate();

  const [familyId, setFamilyId] = useState('');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [dependents, setDependents] = useState<Dependent[]>([]);
  const [loading, setLoading] = useState(true);
  const [startingTemplate, setStartingTemplate] = useState<Template | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const families = await api.getFamilies();
        if (!families?.length) { navigate('/onboarding'); return; }
        const fid = families[0].id;
        setFamilyId(fid);

        const [tmpls, lists, deps] = await Promise.all([
          api.getChecklistTemplates(),
          api.getChecklists(fid),
          api.getDependents(fid),
        ]);
        setTemplates(tmpls as Template[]);
        setChecklists(lists as Checklist[]);
        setDependents((deps as Dependent[]).filter((d) => d.isActive));
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const handleStarted = (checklistId: string) => {
    setStartingTemplate(null);
    navigate(`/checklists/${checklistId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 text-sm">
        Loading…
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10">

      {/* ── Templates ───────────────────────────────────────────────────── */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Start from a template</h2>
        <p className="text-sm text-gray-500 mb-4">
          Pre-built checklists to help you stay prepared. Tick items off as you complete them.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {templates.map((tmpl) => (
            <div key={tmpl.id}>
              <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 text-sm">{tmpl.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{tmpl.description}</p>
                  </div>
                  <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full ${CATEGORY_COLOUR[tmpl.category] ?? 'bg-gray-100 text-gray-600'}`}>
                    {tmpl.itemCount} items
                  </span>
                </div>
                <button
                  onClick={() =>
                    startingTemplate?.id === tmpl.id
                      ? setStartingTemplate(null)
                      : setStartingTemplate(tmpl)
                  }
                  className="btn btn-primary text-sm py-1.5 w-full"
                >
                  {startingTemplate?.id === tmpl.id ? 'Cancel' : 'Start checklist'}
                </button>
              </div>
              {startingTemplate?.id === tmpl.id && (
                <StartChecklistForm
                  template={tmpl}
                  familyId={familyId}
                  dependents={dependents}
                  onSuccess={handleStarted}
                  onCancel={() => setStartingTemplate(null)}
                />
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── Your checklists ──────────────────────────────────────────────── */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Your checklists</h2>

        {checklists.length === 0 ? (
          <p className="text-sm text-gray-400">No checklists yet. Start one from the templates above.</p>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
            {checklists.map((list) => {
              const pct = list.totalCount > 0
                ? Math.round((list.completedCount / list.totalCount) * 100)
                : 0;
              const complete = list.completedCount === list.totalCount && list.totalCount > 0;

              return (
                <button
                  key={list.id}
                  onClick={() => navigate(`/checklists/${list.id}`)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <div className="min-w-0">
                      <span className="text-sm font-medium text-gray-900">{list.title}</span>
                      {list.dependent && (
                        <span className="ml-2 text-xs text-gray-400">· {list.dependent.name}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${CATEGORY_COLOUR[list.category] ?? 'bg-gray-100 text-gray-600'}`}>
                        {CATEGORY_LABEL[list.category] ?? list.category}
                      </span>
                      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full transition-all ${complete ? 'bg-green-500' : 'bg-primary-500'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 shrink-0">
                      {list.completedCount}/{list.totalCount}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
